import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../authenticated-base.component';
import {NgxEchartsDirective, provideEchartsCore} from 'ngx-echarts';
import * as echarts from 'echarts/core';
import {EChartsOption} from 'echarts';
import {BarChart, LineChart} from 'echarts/charts';
import {GraphicComponent, GridComponent, LegendComponent, TitleComponent, TooltipComponent} from 'echarts/components';
import {CanvasRenderer} from 'echarts/renderers';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../services/content-header.service';
import {TitleService} from '../../services/title.service';
import {StatisticsService} from '../../services/api/statistics.service';
import {generateChartOption} from '../../utils/generate-echarts-option';
import {DateTimeSelectorComponent} from '../../components/form-inputs/date-time-selector/date-time-selector.component';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {DateTime} from 'luxon';
import {Backend} from '@generated-backend-types';
import {distinctUntilChanged, takeUntil} from 'rxjs';
import StatisticsTimeframeData = Backend.App.Data.Dashboard.Statistics.StatisticsTimeframeData;

echarts.use([BarChart, GridComponent, CanvasRenderer, LegendComponent, TitleComponent, TooltipComponent, GraphicComponent, LineChart]);

@Component({
  selector: 'app-statistics',
  templateUrl: 'statistics.component.html',
  standalone: true,
  providers: [provideEchartsCore({echarts})],
  imports: [NgxEchartsDirective, DateTimeSelectorComponent, TranslatePipe, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatisticsComponent extends AuthenticatedBaseComponent implements OnInit {
  public chartOptions: EChartsOption[] = [];

  public timeframesForm = this.fb.group({
    timeframe_start_at: this.fb.control<string>('', [Validators.required]),
    timeframe_end_at: this.fb.control<string>('', [Validators.required])
  });

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly statisticsService: StatisticsService,
    private readonly fb: NonNullableFormBuilder,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.timeframesForm.patchValue({
      timeframe_start_at: DateTime.now().minus({month: 1}).startOf('month').toISODate(),
      timeframe_end_at: DateTime.now().minus({month: 1}).endOf('month').toISODate(),
    });

    this.loadStatistics();

    this.timeframesForm.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      distinctUntilChanged()
    ).subscribe(() => this.loadStatistics());
  }

  private loadStatistics() {
    const timeFrameData: StatisticsTimeframeData = {
      timeframe_start_at: DateTime.fromISO(this.timeframesForm.value.timeframe_start_at ?? '').toISODate(),
      timeframe_end_at: DateTime.fromISO(this.timeframesForm.value.timeframe_end_at ?? '').toISODate(),
    };

    this.statisticsService.getStats(timeFrameData).subscribe(res => {
      this.chartOptions = res.data.charts.map(item => generateChartOption(item));
      this.cdr.markForCheck();
    });
  }
}
